import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cue, NodeCue, NodeList, parseSync } from "subtitle";

import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";

export function useCaptions(captionsEnabled = false, captionsURL?: string, position = 0, hasNoAudio = false) {
  const [parsed, setParsed] = useState<NodeList>();
  const [text, setText] = useState("\n\n");
  const [loading, setLoading] = useState(false);
  const { reportError } = useErrorHandler();
  const { t } = useTranslation();

  function getText(cue: Cue): string {
    return cue.text;
  }

  useEffect(() => {
    if (hasNoAudio) {
      const noAudioText = `[${t("noAudioSlide")}]`;
      if (text !== noAudioText) setText(noAudioText);
      return;
    }

    const positionMs = position * 1000;
    const isActive = (cue: NodeCue): boolean => {
      return positionMs >= cue.data.start && positionMs < cue.data.end;
    };

    const cue: NodeCue | undefined = parsed?.find(node => node.type === "cue" && isActive(node)) as NodeCue;
    if (cue) {
      const activeText = getText(cue.data);
      if (activeText !== text) setText(activeText);
    } else if (text !== "\n\n") {
      setText("\n\n");
    }
  }, [position, parsed, hasNoAudio, t, text]);

  const loadCaptions = useCallback(
    async (captionsURL: string) => {
      try {
        setLoading(true);
        setParsed(undefined);
        const response = await fetch(captionsURL);
        const subtitles = await response.text();

        if (subtitles.trim().length === 0) {
          reportError(ErrorSeverity.LOW, "Caption string is empty", { captionsURL });
        }

        setParsed(parseSync(subtitles));
      } catch (error) {
        console.error("Failed to load captions", error);
        reportError(ErrorSeverity.LOW, "Failed to load captions", { captionsURL });
      } finally {
        setLoading(false);
      }
    },
    [reportError],
  );

  useEffect(() => {
    if (captionsEnabled && !hasNoAudio && captionsURL) {
      loadCaptions(captionsURL);
    }
  }, [captionsEnabled, loadCaptions, hasNoAudio, captionsURL]);

  return { text, loadCaptions, loading };
}
